import { driver, type Config, type Driver } from 'driver.js';

/**
 * driver.js keeps ONE global state (config, overlay, active element) no matter how many
 * `driver()` instances exist. Product tours and the Go to element spotlight therefore have to
 * take turns: whoever drives the overlay holds the single session here.
 *
 * - A tour always takes over; a spotlight that was showing is torn down first.
 * - A spotlight never starts over a tour (`claimDriver('spotlight')` returns null).
 * - A session that lost ownership can no longer destroy driver.js, so a stale timer or cleanup
 *   cannot close the session that replaced it.
 */
export type DriverOwnerKind = 'tour' | 'spotlight';

let owner: DriverSession | null = null;

export class DriverSession {
  private instance: Driver | null = null;

  constructor(readonly kind: DriverOwnerKind, private readonly onLost: () => void) {}

  /** True while this session still holds driver.js. */
  get owns(): boolean {
    return owner === this;
  }

  /** Creates the driver.js instance of this session; null once ownership was lost. */
  create(config: Config): Driver | null {
    if (!this.owns) return null;
    this.instance = driver(config);
    return this.instance;
  }

  /** Ends the session. Destroys driver.js only while this session still owns it. */
  release(): void {
    if (!this.owns) return;
    owner = null;
    this.destroyInstance();
  }

  /** Called when another owner takes over: stops our timers first, then removes our overlay
   * while our config is still the global one (driver() replaces it). */
  revoke(): void {
    if (owner === this) owner = null;
    this.onLost();
    this.destroyInstance();
  }

  private destroyInstance(): void {
    const instance = this.instance;
    this.instance = null;
    if (instance?.isActive()) instance.destroy();
  }
}

/** Takes driver.js for a tour or a spotlight. `onLost` runs if a later owner takes over. */
export function claimDriver(kind: DriverOwnerKind, onLost: () => void = () => undefined): DriverSession | null {
  if (kind === 'spotlight' && owner?.kind === 'tour') return null;
  const previous = owner;
  const session = new DriverSession(kind, onLost);
  owner = session;
  previous?.revoke();
  return session;
}

/** Current owner kind, for diagnostics and tests. */
export function driverOwnerKind(): DriverOwnerKind | null {
  return owner?.kind ?? null;
}
